import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { TOUR_STEPS } from "./tourSteps"

const TourContext = createContext(null)

export const STATE_IDLE = "idle"
export const STATE_NAVIGATING = "navigating"
export const STATE_POSITIONING = "positioning"
export const STATE_READY = "ready"

export function TourProvider({ children }) {
  const { props } = usePage()
  const tourStep = props.tourStep  // from inertia_share in App::Coach::BaseController
  const currentStep = tourStep ?? null

  const [tourState, setTourState] = useState(tourStep ? STATE_POSITIONING : STATE_IDLE)
  const [pageHandlers, setPageHandlers] = useState(null)
  const isTourActive = currentStep !== null

  // Track Inertia navigation state
  useEffect(() => {
    const removeStart = router.on("start", () => setTourState(STATE_NAVIGATING))
    const removeFinish = router.on("finish", () => {
      if (isTourActive) setTourState(STATE_POSITIONING)
    })
    return () => { removeStart(); removeFinish() }
  }, [isTourActive])

  // When tourState moves to POSITIONING (page loaded), mark ready after a frame
  // so TourSpotlight has a chance to find the element
  useEffect(() => {
    if (tourState === STATE_POSITIONING) {
      const id = requestAnimationFrame(() => setTourState(STATE_READY))
      return () => cancelAnimationFrame(id)
    }
  }, [tourState])

  // Sync tourState when currentStep changes (URL param driven)
  const prevStep = useRef(currentStep)
  useEffect(() => {
    if (prevStep.current !== currentStep) {
      prevStep.current = currentStep
      if (currentStep !== null) setTourState(STATE_POSITIONING)
      else setTourState(STATE_IDLE)
    }
  }, [currentStep])

  // Call onEnter callbacks for the active step when step is ready
  useEffect(() => {
    if (tourState !== STATE_READY || !currentStep) return
    const stepDef = TOUR_STEPS.find(s => s.step === currentStep)
    if (stepDef?.onEnter) {
      stepDef.onEnter(pageHandlers ?? {})
    }
  }, [currentStep, tourState, pageHandlers])

  function csrfHeaders() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    return { "X-CSRF-Token": token, "Content-Type": "application/json" }
  }

  function navigateToStep(stepNum, path = null) {
    setTourState(STATE_NAVIGATING)
    const url = new URL(path ?? window.location.href, window.location.origin)
    url.searchParams.set("tour", stepNum)
    router.visit(url.toString(), { replace: true, preserveScroll: false })
  }

  function startTour() {
    navigateToStep(1, "/app/coach")
  }

  function nextStep() {
    if (!currentStep || tourState !== STATE_READY) return
    const stepDef = TOUR_STEPS.find(s => s.step === currentStep)
    if (!stepDef) return

    if (stepDef.navigateToArticle) {
      // Special case: fetch sample article, server redirects to article?tour=nextStep
      setTourState(STATE_NAVIGATING)
      const url = new URL("/app/coach/onboarding/sample_article", window.location.origin)
      url.searchParams.set("tour", stepDef.nextStep ?? currentStep + 1)
      router.visit(url.toString(), { method: "post", replace: true })
      return
    }

    if (stepDef.nextPage) {
      navigateToStep(stepDef.nextStep ?? currentStep + 1, stepDef.nextPage)
    } else {
      const next = currentStep + 1
      if (next > TOUR_STEPS.length) {
        completeTour()
      } else {
        navigateToStep(next)
      }
    }
  }

  function goBack() {
    if (!currentStep || tourState !== STATE_READY) return
    if (currentStep <= 1) return

    const prevStepNum = currentStep - 1
    const prevStepDef = TOUR_STEPS.find(s => s.step === prevStepNum)
    if (!prevStepDef) return

    // Going back to the article editor requires the sample_article route because
    // we need an article ID in the URL (/app/coach/articles/:id).
    if (prevStepDef.page === "/app/coach/articles/:id") {
      setTourState(STATE_NAVIGATING)
      const url = new URL("/app/coach/onboarding/sample_article", window.location.origin)
      url.searchParams.set("tour", prevStepNum)
      router.visit(url.toString(), { method: "post", replace: true })
      return
    }

    // For other pages, navigate directly
    if (prevStepDef.page && !prevStepDef.page.includes(":id")) {
      navigateToStep(prevStepNum, prevStepDef.page)
    } else {
      navigateToStep(prevStepNum)
    }
  }

  async function skipTour() {
    try {
      await fetch("/app/coach/onboarding/complete", {
        method: "PATCH",
        headers: csrfHeaders(),
      })
    } catch (e) {} // silent fail — tour re-shows on next login, acceptable

    const url = new URL(window.location.href)
    url.searchParams.delete("tour")
    router.visit(url.toString(), { replace: true, preserveScroll: true })
  }

  async function completeTour() {
    // Await the PATCH so the server sets coach_onboarded_at BEFORE the next
    // Inertia request — prevents the auto-start effect from re-firing because
    // coachOnboarded is still false on the new page load.
    try {
      await fetch("/app/coach/onboarding/complete", {
        method: "PATCH",
        headers: csrfHeaders(),
      })
    } catch (e) {} // silent fail — acceptable

    const url = new URL(window.location.href)
    url.searchParams.delete("tour")
    router.visit(url.toString(), { replace: true, preserveScroll: true })
  }

  // Page components call this to register their imperative handlers
  // Returns a cleanup function (for useEffect return)
  const registerTourHandlers = useCallback((handlers) => {
    setPageHandlers(handlers)
    return () => setPageHandlers(null)
  }, [])

  const value = useMemo(() => ({
    isTourActive,
    currentStep,
    tourState,
    startTour,
    nextStep,
    goBack,
    skipTour,
    completeTour,
    registerTourHandlers,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [isTourActive, currentStep, tourState, registerTourHandlers])

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  )
}

export function useTour() {
  const ctx = useContext(TourContext)
  if (!ctx) throw new Error("useTour must be used within TourProvider")
  return ctx
}

// Used by page components to register their handlers
export function useTourPage() {
  const ctx = useContext(TourContext)
  if (!ctx) throw new Error("useTourPage must be used within TourProvider")
  return { registerTourHandlers: ctx.registerTourHandlers }
}
